import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Play, Pencil } from "lucide-react";
import { api } from "../lib/api";
import { useCurator } from "../lib/useCurator";
import { FadeUp } from "../components/Reveal";
import CoverImage from "../components/CoverImage";
import EntryActions from "../components/EntryActions";
import NoteDialog from "../components/NoteDialog";

const TYPE_LABEL = { book: "Book", film: "Film", tv: "Series", youtube: "Video", essay: "Essay" };
const EASE = [0.16, 1, 0.3, 1];

const ytId = (item) => {
  const a = (item.cover_url || "").match(/vi\/([^/]+)\//);
  if (a) return a[1];
  const b = (item.links?.[0]?.url || "").match(/[?&]v=([^&]+)/);
  return b ? b[1] : null;
};

const Row = ({ label, children, testId }) => (
  <div className="grid grid-cols-3 gap-6 py-4" data-testid={testId}>
    <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-vellum-muted pt-1">{label}</dt>
    <dd className="col-span-2 text-sm text-vellum">{children}</dd>
  </div>
);

export default function LibraryEntry() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [noteItem, setNoteItem] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const curator = useCurator(id);

  useEffect(() => {
    setPlaying(false);
    setItem(null);
    setNotFound(false);
    api.get(`/library/${id}`).then((r) => setItem(r.data)).catch(() => setNotFound(true));
    api.get(`/library/${id}/related`).then((r) => setRelated(r.data)).catch(() => {});
  }, [id]);

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6" data-testid="entry-not-found">
      <p className="overline text-crimson mb-4">Not found</p>
      <p className="font-serif text-4xl text-vellum mb-8">This entry isn't in the library.</p>
      <Link to="/library" className="font-mono text-xs uppercase tracking-[0.2em] px-6 py-3 border border-crimson text-crimson hover:bg-crimson hover:text-onaccent transition-colors">Back to the Library</Link>
    </div>
  );

  if (!item) return <div className="min-h-screen flex items-center justify-center"><p className="font-serif text-3xl text-vellum-muted animate-pulse">Loading…</p></div>;

  const isVideo = item.media_type === "youtube";
  const vid = isVideo ? ytId(item) : null;
  const portrait = item.media_type === "book" || item.media_type === "essay";
  const links = item.links || [];

  return (
    <div data-testid="library-entry-page" className="pt-16 lg:pt-20">
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-10 lg:pt-14 pb-20 lg:pb-28">
        <FadeUp>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <Link to="/library" data-testid="entry-back" className="inline-flex items-center gap-2 overline text-brass hover:text-vellum transition-colors">
              <ArrowLeft className="w-4 h-4" /> The Library
            </Link>
            {curator && (
              <Link
                to={`/admin?tab=books&edit=${item.id}`}
                data-testid="entry-curator-edit"
                title="Open this entry in the editor"
                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 border border-brass/50 text-brass hover:bg-brass hover:text-ink transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" /> Edit this entry
              </Link>
            )}
          </div>
        </FadeUp>

        <div className="mt-10 lg:mt-14 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* LEFT — cover, top-aligned with the title and sticky while reading */}
          {/* `contents` below lg lets the cover, writeup and actions be ordered
              independently while staying one sticky column on desktop */}
          <div className="contents lg:block lg:col-span-4 lg:sticky lg:top-28">
            {isVideo && vid ? (
              <motion.div
                initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}
                className="order-1 lg:order-none relative w-full aspect-video border border-line overflow-hidden"
              >
                {playing ? (
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${vid}?autoplay=1`}
                    title={item.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <button onClick={() => setPlaying(true)} data-testid="entry-play" aria-label={`Play ${item.title}`} className="group relative w-full h-full">
                    <CoverImage src={item.cover_url} alt={item.title} focal={item.cover_focal} size="detail" priority title={item.title} creator={item.creator} year={item.year} />
                    <span className="absolute inset-0 bg-ink/40 group-hover:bg-ink/20 transition-colors flex items-center justify-center">
                      <span className="w-16 h-16 bg-crimson flex items-center justify-center group-hover:scale-105 transition-transform">
                        <Play className="w-6 h-6 text-onaccent ml-0.5" fill="currentColor" />
                      </span>
                    </span>
                  </button>
                )}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}
                className={`order-1 lg:order-none relative self-start w-2/3 sm:w-1/2 lg:w-full max-w-[420px] overflow-hidden border border-line ${portrait ? "aspect-[2/3]" : "aspect-video"}`}
              >
                <CoverImage src={item.cover_url} alt={item.title} focal={item.cover_focal} size="detail" priority title={item.title} creator={item.creator} year={item.year} />
              </motion.div>
            )}

            <div className="order-3 lg:order-none mt-2 lg:mt-8" data-testid="entry-actions">
              <EntryActions item={item} onOpenNote={setNoteItem} labelled />
            </div>
          </div>

          {/* RIGHT — the writeup */}
          <div className="order-2 lg:order-none lg:col-span-8 lg:col-start-6">
            <FadeUp>
              <div className="flex flex-wrap items-center gap-3 mb-5">
                <span className="overline text-crimson">{item.field || TYPE_LABEL[item.media_type]}</span>
                <span className="w-1 h-1 bg-vellum-muted" />
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-vellum-muted">{TYPE_LABEL[item.media_type]} · {item.year}</span>
              </div>
              <h1 className="font-serif text-5xl lg:text-7xl font-light tracking-tight leading-[0.95] text-vellum">{item.title}</h1>
              <p className="mt-4 font-mono text-sm text-brass">{item.creator}</p>
            </FadeUp>

            <FadeUp delay={0.08}>
              <p className="mt-10 font-serif text-2xl lg:text-3xl font-light leading-snug text-vellum/90 italic">{item.note}</p>
            </FadeUp>

            {item.audience && (
              <FadeUp delay={0.12}>
                <div className="mt-8 border-l-2 border-crimson pl-5">
                  <p className="overline mb-2 text-brass">Why you should read this</p>
                  <p className="text-lg text-vellum-muted leading-relaxed">{item.audience}</p>
                </div>
              </FadeUp>
            )}

            {/* Hairline record: the archival detail table */}
            <FadeUp delay={0.16}>
              <dl className="mt-12 border-t border-line divide-y divide-line" data-testid="entry-record">
                <Row label="Field" testId="entry-record-field">{item.field || "—"}</Row>
                <Row label="Form" testId="entry-record-form">{item.form || TYPE_LABEL[item.media_type]}</Row>
                <Row label="Published" testId="entry-record-year">
                  {Number.isFinite(item.year) ? (item.year < 0 ? `${Math.abs(item.year)} BCE` : item.year) : "—"}
                </Row>
                {item.tags?.length > 0 && (
                  <Row label="Themes" testId="entry-record-themes">
                    <span className="flex flex-wrap gap-2">
                      {[...new Set(item.tags)].map((t) => (
                        <span key={t} className="font-mono text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-line text-vellum-muted">{t}</span>
                      ))}
                    </span>
                  </Row>
                )}
                {links.length > 1 && (
                  <Row label="Also at" testId="entry-record-links">
                    <span className="flex flex-col divide-y divide-line/70 -my-1">
                      {links.slice(1).map((l, i) => (
                        <a
                          key={`${l.url}-${i}`}
                          href={l.url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`entry-link-${i + 1}`}
                          className="group flex items-center justify-between gap-4 py-2.5 text-vellum hover:text-crimson transition-colors"
                        >
                          <span className="font-mono text-[11px] uppercase tracking-[0.2em]">{l.label}</span>
                          <ArrowUpRight className="w-4 h-4 shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </a>
                      ))}
                    </span>
                  </Row>
                )}
              </dl>
            </FadeUp>

            {links.length > 0 && (
              <FadeUp delay={0.2}>
                <a
                  href={links[0].url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="entry-primary-link"                  className="group mt-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 bg-vellum text-ink hover:bg-crimson hover:text-onaccent transition-colors"
                >
                  {links[0].label} <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </a>
              </FadeUp>
            )}
          </div>
        </div>
      </section>

      {/* You may also like */}
      {related.length > 0 && (
        <section className="bg-ink-800 border-t border-line py-20 lg:py-28" data-testid="entry-related">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
            <FadeUp>
              <p className="overline mb-10 text-crimson">
                More from {item.field || "the library"}
              </p>
            </FadeUp>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
              {related.map((r, i) => (
                <FadeUp key={r.id} delay={i * 0.06}>
                  <Link to={`/library/${r.id}`} data-testid={`related-${r.id}`} className="group block">
                    <div className={`relative overflow-hidden border border-line mb-4 ${r.media_type === "book" || r.media_type === "essay" ? "aspect-[2/3]" : "aspect-video"}`}>
                      <CoverImage src={r.cover_url} alt={r.title} focal={r.cover_focal} title={r.title} creator={r.creator} year={r.year} />
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-crimson">{TYPE_LABEL[r.media_type]}</span>
                    <h3 className="font-serif text-xl text-vellum group-hover:text-brass transition-colors leading-tight">{r.title}</h3>
                    <p className="font-mono text-xs text-vellum-muted mt-1">{r.creator}</p>
                  </Link>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}

      {noteItem && <NoteDialog item={noteItem} onClose={() => setNoteItem(null)} />}
    </div>
  );
}
